import { useState } from "react";
import { Link } from "react-router-dom";
import { useSiteSettings } from "@/lib/useSiteSettings";

const INTRANET_URL = import.meta.env.VITE_ADMIN_URL || "http://localhost:15173/login";

export default function Footer() {
  const siteConfig = useSiteSettings();
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubscribe = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    const formData = new URLSearchParams();
    formData.append("email", email);
    try {
      await fetch("https://readdy.ai/api/form/d7np93tbmnmv4i2uk8k0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: formData.toString(),
      });
      setSubmitted(true);
      setEmail("");
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <footer className="bg-sky-900 text-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-14">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-10 mb-12">
          {/* Brand */}
          <div className="md:col-span-1">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-sky-500 text-white">
                <i className="ri-drop-fill text-xl"></i>
              </div>
              <div>
                <span className="font-bold text-base block" style={{ fontFamily: "'Poppins', sans-serif" }}>
                  {siteConfig.aprName}
                </span>
                <span className="text-xs text-sky-300 block">Agua Potable Rural</span>
              </div>
            </div>
            <p className="text-sky-200 text-sm leading-relaxed">
              Comprometidos con el suministro de agua potable de calidad para nuestra comunidad rural.
            </p>
            <a
              href={INTRANET_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 mt-2 text-[11px] text-sky-400/80 hover:text-sky-200 transition-colors cursor-pointer"
              aria-label="Acceso a intranet"
            >
              <i className="ri-lock-line text-xs"></i>
              Intranet
            </a>
          </div>

          {/* Links */}
          <div>
            <h4 className="font-semibold text-sm uppercase tracking-wider text-sky-300 mb-4">Navegación</h4>
            <ul className="space-y-2">
              {[
                { label: "Inicio", path: "/" },
                { label: "Noticias", path: "/noticias" },
                { label: "Actividades", path: "/actividades" },
                { label: "Comunicados", path: "/comunicados" },
              ].map((l) => (
                <li key={l.path}>
                  <Link to={l.path} className="text-sky-200 hover:text-white text-sm transition-colors cursor-pointer">
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-semibold text-sm uppercase tracking-wider text-sky-300 mb-4">Información</h4>
            <ul className="space-y-2">
              {[
                { label: "Sobre el APR", path: "/sobre-el-apr" },
                { label: "Contacto", path: "/contacto" },
                { label: "Portal usuarios", path: siteConfig.portalUrl },
                { label: "Pagar cuenta", path: siteConfig.payUrl },
              ].map((l) => (
                <li key={l.label}>
                  <a href={l.path} className="text-sky-200 hover:text-white text-sm transition-colors cursor-pointer">
                    {l.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Newsletter */}
          <div>
            <h4 className="font-semibold text-sm uppercase tracking-wider text-sky-300 mb-4">Boletín informativo</h4>
            <p className="text-sky-200 text-sm mb-4">Recibe noticias y comunicados directamente en tu correo.</p>
            {submitted ? (
              <div className="bg-green-500/20 border border-green-400/30 rounded-xl px-4 py-3 text-green-300 text-sm">
                <i className="ri-check-line mr-2"></i>¡Suscripción exitosa!
              </div>
            ) : (
              <form
                data-readdy-form
                onSubmit={handleSubscribe}
                className="flex flex-col gap-2"
              >
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Tu correo electrónico"
                  required
                  className="px-4 py-2.5 rounded-xl bg-sky-800 border border-sky-700 text-white placeholder-sky-400 text-sm focus:outline-none focus:border-sky-400"
                />
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-sky-500 hover:bg-sky-400 text-white text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
                >
                  {submitting ? "Enviando..." : <><i className="ri-send-plane-line"></i> Suscribirse</>}
                </button>
              </form>
            )}
          </div>
        </div>

        <div className="border-t border-sky-800 pt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sky-400 text-sm">
            © 2026 {siteConfig.aprFullName}. Todos los derechos reservados.
          </p>
          <div className="flex items-center gap-4">
            <a
              href={`https://wa.me/${siteConfig.whatsapp}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-sky-300 hover:text-white text-sm transition-colors cursor-pointer"
            >
              <i className="ri-whatsapp-line text-lg"></i>
              WhatsApp
            </a>
            <span className="text-sky-700">|</span>
            <span className="text-sky-400 text-sm">{siteConfig.schedule}</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
